/**
 * TanStack Query слой домена «тех. операции» (AK-525).
 *
 * До этого весь CRUD операций и работ жил на бэкенде и НЕ вызывался из веба
 * ни разу — интерфейс операций был полностью read-only. Здесь собраны все
 * write-пути, чтобы страницы не дублировали url'ы и инвалидацию кэша.
 *
 * Эндпоинты (backend `api/v1/operations.py`, `api/v1/works.py`):
 *   GET    /api/v1/operations/{id}
 *   PATCH  /api/v1/operations/{id}
 *   DELETE /api/v1/operations/{id}          — архивация
 *   POST   /api/v1/fields/{fieldId}/operations
 *   GET    /api/v1/operations/{id}/works
 *   POST   /api/v1/operations/{id}/works
 *   PATCH  /api/v1/works/{id}
 *   DELETE /api/v1/works/{id}               — архивация
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface Operation {
  id: string;
  field_id: string;
  field_name?: string | null;
  sowing_id?: string | null;
  crop_id?: string | null;
  crop_name?: string | null;
  company_op_type_id: string;
  op_type_local_name?: string | null;
  name: string;
  planned_start_date?: string | null;
  planned_end_date?: string | null;
  actual_start_date?: string | null;
  actual_end_date?: string | null;
  planned_area_ha?: number | null;
  actual_area_ha?: number | null;
  lifecycle_state: string;
  /** AK-524: куда можно перевести статус (граф живёт на бэке). */
  allowed_lifecycle_states?: string[] | null;
  was_rescheduled: boolean;
  display_status?: string | null;
  works_total_area_ha?: number | null;
  comment?: string | null;
}

export interface Work {
  id: string;
  operation_id: string;
  executor_user_id?: string | null;
  executor_name?: string | null;
  machine_id?: string | null;
  machine_name_text?: string | null;
  work_date: string;
  area_ha: number;
  comment?: string | null;
}

export interface CompanyOpType {
  id: string;
  company_id: string;
  local_name: string;
  local_code_1c?: string | null;
  ref_id?: string | null;
  ref_code?: string | null;
  ref_name_ru?: string | null;
}

export interface OperationPatch {
  name?: string;
  company_op_type_id?: string;
  planned_start_date?: string | null;
  planned_end_date?: string | null;
  actual_start_date?: string | null;
  actual_end_date?: string | null;
  planned_area_ha?: number | null;
  actual_area_ha?: number | null;
  lifecycle_state?: string;
  comment?: string | null;
}

export interface OperationCreateBody extends OperationPatch {
  field_id: string;
  company_op_type_id: string;
  name: string;
}

export interface WorkBody {
  executor_name_text?: string | null;
  machine_name_text?: string | null;
  work_date: string;
  area_ha: number;
  comment?: string | null;
}

export interface FieldOption {
  id: string;
  name: string;
  farm_name?: string | null;
  area_ha?: number | null;
}

interface FieldsResponse {
  total: number;
  items: FieldOption[];
}

export const operationKeys = {
  one: (id: string) => ['operation', id] as const,
  works: (id: string) => ['operation-works', id] as const,
  ofField: (fieldId: string) => ['field-operations', fieldId] as const,
  list: () => ['operations'] as const,
  companyTypes: () => ['company-operation-types'] as const,
};

/**
 * Поля для выбора при создании операции «из списка операций», где контекста
 * поля нет. Поиск выполняется на сервере (`?search=`) — в хозяйстве могут быть
 * тысячи полей, тянуть их все в Select бессмысленно и медленно.
 */
export function useFieldOptions(
  search: string,
  enabled: boolean,
): UseQueryResult<FieldOption[], Error> {
  return useQuery<FieldOption[], Error>({
    queryKey: ['operation-field-options', search] as const,
    queryFn: async () => {
      const qs = new URLSearchParams({ limit: '50', offset: '0' });
      if (search.trim()) qs.set('search', search.trim());
      const resp = await api.get<FieldsResponse>(`/api/v1/fields?${qs}`);
      return resp.items ?? [];
    },
    enabled,
    staleTime: 60_000,
  });
}

export function useOperation(opId: string): UseQueryResult<Operation, Error> {
  return useQuery<Operation, Error>({
    queryKey: operationKeys.one(opId),
    queryFn: () => api.get<Operation>(`/api/v1/operations/${opId}`),
    enabled: !!opId,
  });
}

export function useOperationWorks(opId: string): UseQueryResult<Work[], Error> {
  return useQuery<Work[], Error>({
    queryKey: operationKeys.works(opId),
    queryFn: () => api.get<Work[]>(`/api/v1/operations/${opId}/works`),
    enabled: !!opId,
  });
}

/** Локальные типы операций компании — источник выбора «что делаем». */
export function useCompanyOpTypes(): UseQueryResult<CompanyOpType[], Error> {
  return useQuery<CompanyOpType[], Error>({
    queryKey: operationKeys.companyTypes(),
    queryFn: () => api.get<CompanyOpType[]>('/api/v1/company-operation-types'),
    staleTime: 5 * 60_000,
  });
}

/**
 * Инвалидация после любой записи: сама операция, её работы, список операций
 * поля (виджет карточки) и общий список `/operations`.
 *
 * Работы влияют на операцию не только через сумму площадей (триггер
 * `recompute_op_works_area`), но и через авто-переход `pending → in_progress`
 * (AK-524) — поэтому операцию перечитываем всегда, а не «только при правке».
 */
function useOperationInvalidation() {
  const qc = useQueryClient();
  return (opId: string, fieldId?: string | null) => {
    void qc.invalidateQueries({ queryKey: operationKeys.one(opId) });
    void qc.invalidateQueries({ queryKey: operationKeys.works(opId) });
    void qc.invalidateQueries({ queryKey: operationKeys.list() });
    if (fieldId) {
      void qc.invalidateQueries({ queryKey: operationKeys.ofField(fieldId) });
    }
  };
}

export function useUpdateOperation(opId: string, fieldId?: string | null) {
  const invalidate = useOperationInvalidation();
  return useMutation<Operation, Error, OperationPatch>({
    mutationFn: (body) => api.patch<Operation>(`/api/v1/operations/${opId}`, body),
    onSuccess: () => invalidate(opId, fieldId),
  });
}

export function useCreateOperation(fieldId: string) {
  const invalidate = useOperationInvalidation();
  return useMutation<Operation, Error, OperationCreateBody>({
    mutationFn: (body) =>
      api.post<Operation>(`/api/v1/fields/${fieldId}/operations`, body),
    onSuccess: (op) => invalidate(op.id, fieldId),
  });
}

/**
 * Создание операции, когда поле выбирается прямо в форме (вход из общего
 * списка операций). Отличается от `useCreateOperation` только тем, что
 * `field_id` приходит в теле, а не фиксирован хуком.
 */
export function useCreateOperationAnyField() {
  const invalidate = useOperationInvalidation();
  return useMutation<Operation, Error, OperationCreateBody>({
    mutationFn: ({ field_id, ...body }) =>
      api.post<Operation>(`/api/v1/fields/${field_id}/operations`, {
        field_id,
        ...body,
      }),
    onSuccess: (op) => invalidate(op.id, op.field_id),
  });
}

export function useArchiveOperation(fieldId?: string | null) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (opId) => api.delete<void>(`/api/v1/operations/${opId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: operationKeys.list() });
      if (fieldId) {
        void qc.invalidateQueries({ queryKey: operationKeys.ofField(fieldId) });
      }
    },
  });
}

export function useCreateWork(opId: string, fieldId?: string | null) {
  const invalidate = useOperationInvalidation();
  return useMutation<Work, Error, WorkBody>({
    mutationFn: (body) => api.post<Work>(`/api/v1/operations/${opId}/works`, body),
    onSuccess: () => invalidate(opId, fieldId),
  });
}

export function useUpdateWork(opId: string, fieldId?: string | null) {
  const invalidate = useOperationInvalidation();
  return useMutation<Work, Error, { workId: string; body: Partial<WorkBody> }>({
    mutationFn: ({ workId, body }) => api.patch<Work>(`/api/v1/works/${workId}`, body),
    onSuccess: () => invalidate(opId, fieldId),
  });
}

export function useArchiveWork(opId: string, fieldId?: string | null) {
  const invalidate = useOperationInvalidation();
  return useMutation<void, Error, string>({
    mutationFn: (workId) => api.delete<void>(`/api/v1/works/${workId}`),
    onSuccess: () => invalidate(opId, fieldId),
  });
}
